import {
    ChangeDetectorRef,
    Component,
    inject,
    input,
    OnDestroy,
    OnInit,
    signal,
    viewChild
} from '@angular/core';
import { DocumentViewerDownloadService } from '@trade-platform/ui-shared';
import { OrderFormCommentsService } from '../order-comments/order-form-comments.helper';
import { AixAbstractUploadComponent } from '../abstract-upload';
import {
    AixFilelistComponent,
    AixRequiredFilesMenuOption,
    AixUploadedFilesMenuOption
} from './filelist/filelist';
import { ENVIRONMENT, IEnvironment } from '@trade-platform/ui-utils';
import { BaseOrdersStoreFacade, ORDERS_STORE_FACADE } from '../base.orders.store.facade';
import { AsyncPipe } from '@angular/common';
import { AixUploadDocumentComponent } from '@advisor-ui/app-components';
import { AixLabelUploadedFileDocumentsComponent } from './file-documents/label-uploaded-file-documents';
import { AixLabelUploadedFormDocumentsComponent } from './form-documents/label-uploaded-form-documents';
import {
    AixButtonComponent,
    AixCheckboxComponent,
    AixDataTestingDirective,
    AixModalComponent,
    AixTooltipDirective,
    BUTTON_TYPE
} from '@trade-platform/ui-components';
import { FormsModule } from '@angular/forms';
import {
    getRequiredFilesToUpload,
    getUnfinishedRequiredUploads,
    getUploadedSupplementalFiles
} from '@advisor-ui/orders';
import { orderStatus } from '@trade-platform/lib-enums';
import { ActivatedRoute } from '@angular/router';

@Component({
    selector: 'aix-file-upload',
    templateUrl: './file-upload.html',
    styleUrls: ['./file-upload.scss'],
    standalone: true,
    imports: [
        AixDataTestingDirective,
        AixUploadDocumentComponent,
        AixLabelUploadedFileDocumentsComponent,
        AixLabelUploadedFormDocumentsComponent,
        AixFilelistComponent,
        AixModalComponent,
        AsyncPipe,
        AixCheckboxComponent,
        FormsModule,
        AixButtonComponent,
        AixTooltipDirective
    ]
})
export class AixFileUploadComponent
    extends AixAbstractUploadComponent
    implements OnInit, OnDestroy
{
    environment: IEnvironment;
    storeFacade: BaseOrdersStoreFacade;
    docDownloader: DocumentViewerDownloadService;
    commentsService: OrderFormCommentsService;
    cd: ChangeDetectorRef;
    route: ActivatedRoute;

    uploadButtonType = BUTTON_TYPE.link;

    filelistRef = viewChild<AixFilelistComponent>('filelistRef');

    innerUpload = input<boolean>(true);
    enableIgnore = input<boolean>(false);
    isReadOnly = input<boolean>(false);
    type = input<string>(this.fileType.supplemental);
    instructionalText = input<string>('');
    fileListPosition = signal<string>('top');

    unfinishedRequiredUploads = true;
    supplementalFiles: AixUploadedFilesMenuOption[] = [];
    requiredSupplementalFiles: AixRequiredFilesMenuOption[] = [];

    acceptIgnoreRequired = (button: string) =>
        !this.acknowledgeIgnoreRequired && button !== 'Cancel';

    /** Inserted by Angular inject() migration for backwards compatibility */
    constructor(...args: unknown[]);

    constructor() {
        const environment = inject<IEnvironment>(ENVIRONMENT);
        const storeFacade = inject<BaseOrdersStoreFacade>(ORDERS_STORE_FACADE);
        const docDownloader = inject(DocumentViewerDownloadService);
        const commentsService = inject(OrderFormCommentsService);
        const cd = inject(ChangeDetectorRef);
        const route = inject(ActivatedRoute);

        super(environment, storeFacade, docDownloader, commentsService, cd);
        this.environment = environment;
        this.storeFacade = storeFacade;
        this.docDownloader = docDownloader;
        this.commentsService = commentsService;
        this.cd = cd;
        this.route = route;
    }

    ngOnInit() {
        super.ngOnInit();

        this.subscriptions.push(
            this.route.data.subscribe(data => {
                // aix-filelist component position set in router "purchase.orders.routes.ts"
                this.fileListPosition.set(data['fileListPosition'] ?? 'top');
            }),
            this.storeFacade.orderSuccess$.subscribe(order => {
                this.unfinishedRequiredUploads = getUnfinishedRequiredUploads(order) > 0;
                this.supplementalFiles = getUploadedSupplementalFiles(order);
                this.requiredSupplementalFiles = getRequiredFilesToUpload(order);
            })
        );
    }

    protected readonly orderStatus = orderStatus;
}
